import { useLayoutEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * One detached host element. Portal a subtree into it, then append the host
 * into whichever slot is current. The React tree stays mounted when the slot
 * moves, unmounts, or is missing. Re-attaching does not call focus().
 */
export function useStablePortalHost(slot: HTMLElement | null): HTMLElement | null {
  const [host] = useState<HTMLElement | null>(() => {
    if (typeof document === 'undefined') return null
    const el = document.createElement('div')
    // The portaled child keeps its own flex classes against the slot.
    el.style.display = 'contents'
    return el
  })

  useLayoutEffect(() => {
    if (!host) return
    if (!slot) {
      host.remove()
      return
    }
    if (host.parentNode !== slot) slot.appendChild(host)
  }, [host, slot])

  useLayoutEffect(() => {
    if (!host) return
    return () => host.remove()
  }, [host])

  return host
}

/** Portal `children` through `useStablePortalHost(slot)`. */
export function StablePortal({
  slot,
  children,
}: {
  slot: HTMLElement | null
  children: ReactNode
}) {
  const host = useStablePortalHost(slot)
  if (!host) return null
  return createPortal(children, host)
}
